import React from 'react';
import { Heart, Mail, Phone, MapPin } from 'lucide-react';
import { mockData } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';

const EventimeFooter = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer 
      style={{
        background: 'var(--brand-dark)',
        color: 'white',
        padding: 'var(--spacing-giant) 0 var(--spacing-large) 0'
      }}
    >
      <div className="container">
        {/* Main Footer Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--spacing-xl)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          {/* Company Info */}
          <div>
            <h3 
              className="heading-3" 
              style={{ 
                color: 'white',
                marginBottom: 'var(--spacing-medium)'
              }}
            >
              {mockData.company.name}
            </h3>
            <p 
              className="body-medium" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                opacity: '0.9'
              }}
            >
              {t('footer.tagline')}
            </p>
            <p 
              className="body-medium" 
              style={{ 
                opacity: '0.8',
                lineHeight: '1.6'
              }}
            >
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="heading-3" 
              style={{ 
                color: 'white',
                marginBottom: 'var(--spacing-medium)'
              }}
            >
              {t('footer.navigation')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-small)' }}>
              {[
                { name: t('nav.home'), href: '#hero' },
                { name: t('nav.services'), href: '#services' },
                { name: t('nav.about'), href: '#about' },
                { name: t('nav.expo'), href: '#expo' },
                { name: t('nav.events'), href: '#upcoming-events' },
                { name: t('nav.career'), href: '#career' }
              ].map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.querySelector(item.href);
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    opacity: '0.8',
                    padding: '4px 0',
                    transition: 'opacity 0.2s ease',
                    fontSize: '1rem'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.8'}
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 
              className="heading-3" 
              style={{ 
                color: 'white',
                marginBottom: 'var(--spacing-medium)'
              }}
            >
              {t('footer.services')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-small)' }}>
              {[
                { name: t('services.items.sports.title'), href: '#services' },
                { name: t('services.items.esports.title'), href: '#services' },
                { name: t('expo.title'), href: '#expo' }
              ].map((service, index) => (
                <a
                  key={index}
                  href={service.href}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.querySelector(service.href);
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    opacity: '0.8',
                    padding: '4px 0',
                    transition: 'opacity 0.2s ease',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    lineHeight: '1.4'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.8'}
                >
                  {service.name}
                </a>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 
              className="heading-3" 
              style={{ 
                color: 'white',
                marginBottom: 'var(--spacing-medium)'
              }}
            >
              {t('footer.contact')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-medium)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-small)' }}>
                <Mail size={18} style={{ opacity: '0.8' }} />
                <span style={{ opacity: '0.8' }}>info@eventimeofficial.com</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-small)' }}>
                <Phone size={18} style={{ opacity: '0.8' }} />
                <span style={{ opacity: '0.8' }}>+62 851-7964-9711</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-small)' }}>
                <MapPin size={18} style={{ opacity: '0.8' }} />
                <span style={{ opacity: '0.8' }}>Jakarta, Indonesia</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div 
          style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            paddingTop: 'var(--spacing-large)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--spacing-medium)'
          }}
        >
          <div style={{ fontSize: '0.9rem', lineHeight: '1.6', opacity: '0.8' }}>
                      © {currentYear} eventimeofficial.com. Dibuat dengan ❤️ untuk komunitas Indonesia
                    </div>
          
          <button
            onClick={handleScrollToTop}
            style={{
              background: 'transparent',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '0.9rem'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            {t('footer.backToTop')}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default EventimeFooter;
